import { type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-red text-white hover:bg-brand-coral active:bg-brand-red-deep',
  secondary:
    'border border-border-default text-text-primary hover:border-brand-red hover:text-brand-red',
  ghost:
    'text-text-secondary hover:text-text-primary hover:bg-surface-elevated',
  danger: 'bg-error/10 text-error hover:bg-error/20',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-10 px-6 text-sm',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  ...props
}: ButtonProps): ReactNode {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center rounded-[10px] font-medium',
        'transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-brand-red/30',
        'disabled:opacity-50 disabled:pointer-events-none',
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...props}
    />
  );
}
